/** Read-out metrics for a mechanism: link lengths, Grashof, transmission angle. */

import type { MechanismSpec } from "../kinematics/types.js";
import { grashof, type GrashofInfo } from "../kinematics/factories.js";
import { traceCoupler } from "../kinematics/trace.js";

export interface MechanismReport {
  grashof: GrashofInfo;
  links: Array<{ name: string; length: number }>;
  /** Transmission angle range over the cycle, in degrees (0..90 is best). */
  transmissionDeg: { min: number; max: number } | null;
  convergedFraction: number;
  couplerBBox: { w: number; h: number };
}

/** Joint shared by `bars[0]` and `bars[1]` — the transmission joint. */
function sharedJoint(a: [number, number, number], b: [number, number, number]): number {
  if (a[0] === b[0] || a[0] === b[1]) return a[0];
  if (a[1] === b[0] || a[1] === b[1]) return a[1];
  return -1;
}

export function analyze(spec: MechanismSpec): MechanismReport {
  const g = grashof(spec);
  const links: Array<{ name: string; length: number }> = [
    { name: "crank", length: spec.driver.length },
  ];
  spec.bars.forEach((b, i) => links.push({ name: `link ${i + 2}`, length: b[2] }));
  links.push({
    name: "ground",
    length: Math.hypot(
      spec.grounds[0][0] - spec.grounds[1][0],
      spec.grounds[0][1] - spec.grounds[1][1],
    ),
  });

  const tr = traceCoupler(spec, { samples: 120 });
  let tmin = Infinity;
  let tmax = -Infinity;
  const jt = sharedJoint(spec.bars[0], spec.bars[1]);
  const other0 = spec.bars[0][0] === jt ? spec.bars[0][1] : spec.bars[0][0];
  const other1 = spec.bars[1][0] === jt ? spec.bars[1][1] : spec.bars[1][0];

  for (let i = 0; i < tr.solutions.length; i++) {
    if (!tr.converged[i]) continue;
    const p = tr.solutions[i].positions;
    const v0 = [p[other0][0] - p[jt][0], p[other0][1] - p[jt][1]];
    const v1 = [p[other1][0] - p[jt][0], p[other1][1] - p[jt][1]];
    const c =
      (v0[0] * v1[0] + v0[1] * v1[1]) /
      ((Math.hypot(v0[0], v0[1]) || 1e-9) * (Math.hypot(v1[0], v1[1]) || 1e-9));
    let ang = (Math.acos(Math.max(-1, Math.min(1, c))) * 180) / Math.PI;
    if (ang > 90) ang = 180 - ang; // fold to the acute transmission angle
    tmin = Math.min(tmin, ang);
    tmax = Math.max(tmax, ang);
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of tr.curve) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }

  return {
    grashof: g,
    links,
    transmissionDeg: Number.isFinite(tmin) ? { min: tmin, max: tmax } : null,
    convergedFraction: tr.convergedFraction,
    couplerBBox: { w: maxX - minX, h: maxY - minY },
  };
}
